/**
 * UUID v4 generator with graceful fallback.
 *
 * Why this exists: `crypto.randomUUID()` is part of the Web Crypto API
 * and only available when ALL of these are true:
 *   - Browser is recent (Chrome 92+, Firefox 95+, Safari 15.4+)
 *   - Page is loaded over HTTPS or localhost (a "secure context")
 *
 * Older Safari (especially mobile iOS Safari < 15.4), embedded webviews,
 * and some sandboxed iframe contexts don't have it. When the function
 * isn't available, the original `crypto.randomUUID()` call throws
 * `crypto.randomUUID is not a function` and the form submit fails
 * with no useful error.
 *
 * This helper:
 *   1. Uses `crypto.randomUUID()` when available (the fast path).
 *   2. Falls back to `crypto.getRandomValues()` to build a v4 manually —
 *      same cryptographic strength, broader browser coverage.
 *   3. Last-resort `Math.random()` fallback for environments where
 *      neither Web Crypto API exists. NOT cryptographically secure;
 *      acceptable for client-side draft IDs that are reconciled
 *      server-side.
 *
 * Use this everywhere instead of `crypto.randomUUID()` directly.
 */
export function randomUUID(): string {
  // Fast path — modern browsers.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback 1 — manual v4 from getRandomValues (broad browser support).
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // Set version (4) and variant (10) bits per RFC 4122.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort — Math.random (NOT cryptographically secure).
  // Acceptable for draft IDs since they're reconciled server-side.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
